import React from 'react';

const ArchitectureOverview = () => {
  const layers = [
    {
      name: 'Client Layer',
      color: '#3B82F6',
      components: [
        { name: 'Web Browser', desc: 'React SPA (Admin Panel, User Dashboard, Send SMS)', tech: 'React 18 + TailwindCSS + Lucide Icons' },
        { name: 'HTTP API Client', desc: 'External systems sending SMS via REST API', tech: 'POST/GET with API Key auth' },
        { name: 'SMPP Client', desc: 'External SMPP applications binding to inbound server', tech: 'SMPP 3.4 Protocol (TRX/TX/RX)' },
      ]
    },
    {
      name: 'Edge Layer',
      color: '#06B6D4',
      components: [
        { name: 'Cloudflare', desc: 'DNS, SSL termination, DDoS protection, CDN', tech: 'Domain: mysmpp.in' },
        { name: 'Nginx', desc: 'Reverse proxy, rate limiting, security headers, static files', tech: 'Port 80/443 → Backend 8092' },
      ]
    },
    {
      name: 'Application Layer',
      color: '#10B981',
      components: [
        { name: 'Backend API Server', desc: 'Main REST API — auth, admin, SMS submission, SMPP management', tech: 'Go (Gin) • Port 8092 • gosmpp-backend.service' },
        { name: 'SMPP Inbound Server', desc: 'Accepts SMPP binds from external clients', tech: 'Go • Port 2776 • Part of backend' },
        { name: 'SMS Ingest Service', desc: 'Accepts SMS from all channels, writes to ingest queue', tech: 'Go • Embedded in backend' },
      ]
    },
    {
      name: 'Queue Layer',
      color: '#F97316',
      components: [
        { name: 'Kafka Broker', desc: 'Primary SMS queue — sms.requests, sms.retries, sms.dlrs, sms.deadletter', tech: 'Docker • Port 9092 • 3 partitions/topic' },
        { name: 'Zookeeper', desc: 'Kafka cluster coordination', tech: 'Docker • Port 2181' },
      ]
    },
    {
      name: 'Worker Layer',
      color: '#F59E0B',
      components: [
        { name: 'HTTP Submit Worker', desc: 'Kafka consumer — dedup, balance gate, HTTP delivery, batch DB writes', tech: 'Go • http-submit-worker.service' },
        { name: 'SMPP Worker', desc: 'Kafka consumer — SMPP delivery, outbound connections, DLR processing', tech: 'Go • smpp-worker.service' },
        { name: 'Background Workers', desc: 'Reporting aggregator, DLR callback, log cleanup (run inside backend)', tech: 'Go goroutines • gosmpp-backend' },
      ]
    },
    {
      name: 'Data Layer',
      color: '#EF4444',
      components: [
        { name: 'MariaDB 10.11', desc: '8 databases: primary, reporting (partitioned), worker, logs, alerts, KB, migration, support', tech: 'Port 3306 • ~130 tables • 8GB buffer pool' },
        { name: 'Redis 7', desc: 'Sessions, balance (Lua atomic), TPS, routing cache, dedup, validation cache', tech: 'Port 6379 • In-memory • fail-closed for balance' },
      ]
    },
    {
      name: 'External Layer',
      color: '#8B5CF6',
      components: [
        { name: 'SMPP Operators', desc: 'Outbound SMPP connections to telecom operators for SMS delivery', tech: 'e.g. way2trmpg (45.114.140.155:4444)' },
        { name: 'HTTP Operators', desc: 'HTTP API endpoints of telecom operators', tech: 'REST API with operator credentials' },
        { name: 'Telegram', desc: 'Alert delivery via Telegram Bot API', tech: 'Bot API for system alerts' },
      ]
    },
  ];

  const dataFlows = [
    { from: 'Browser', to: 'Cloudflare', label: 'HTTPS', color: '#3B82F6' },
    { from: 'Cloudflare', to: 'Nginx', label: 'HTTP (port 80)', color: '#06B6D4' },
    { from: 'Nginx', to: 'Backend', label: '/api/v1/* → port 8092', color: '#10B981' },
    { from: 'Backend', to: 'Redis', label: 'Balance check + sessions', color: '#EF4444' },
    { from: 'Backend', to: 'Kafka', label: 'Produce to sms.requests (primary)', color: '#F97316' },
    { from: 'Backend', to: 'MariaDB', label: 'INSERT sms_ingest_requests (fallback)', color: '#F59E0B' },
    { from: 'HTTP Worker', to: 'Kafka', label: 'Consume sms.requests (HTTP)', color: '#F97316' },
    { from: 'SMPP Worker', to: 'Kafka', label: 'Consume sms.requests (SMPP)', color: '#F97316' },
    { from: 'Workers', to: 'Redis', label: 'Dedup (SHA256) + Balance reserve (Lua)', color: '#EF4444' },
    { from: 'Workers', to: 'MariaDB', label: 'Batch INSERT sms_reports_partitioned', color: '#10B981' },
    { from: 'HTTP Worker', to: 'HTTP Operator', label: 'HTTP POST/GET', color: '#8B5CF6' },
    { from: 'SMPP Worker', to: 'SMPP Operator', label: 'submit_sm PDU', color: '#8B5CF6' },
    { from: 'Operator', to: 'SMPP Worker', label: 'deliver_sm (DLR)', color: '#EC4899' },
    { from: 'Backend', to: 'User DLR URL', label: 'DLR callback HTTP POST', color: '#EC4899' },
  ];

  const serverSpecs = [
    { label: 'Server', value: 'Dedicated Server (OVH)' },
    { label: 'IP', value: '148.113.24.219' },
    { label: 'OS', value: 'Linux (CentOS/RHEL)' },
    { label: 'SSH Port', value: '1985' },
    { label: 'Go Version', value: '1.21+ (/usr/local/go/bin/go)' },
    { label: 'MariaDB', value: '10.11 (Port 3306, 8GB buffer pool)' },
    { label: 'Redis', value: '7.x (Port 6379)' },
    { label: 'Kafka', value: 'Docker (Port 9092, UI on 8091)' },
    { label: 'Nginx', value: 'Latest (Port 80/443)' },
    { label: 'Domain', value: 'mysmpp.in (Cloudflare DNS)' },
    { label: 'Capacity', value: '~5M-10M SMS/day (single server)' },
  ];

  const systemdServices = [
    { name: 'gosmpp-backend.service', binary: '/opt/GoSMPP/bin/backend', desc: 'Main API + SMPP inbound + background workers', port: '8092, 2776', status: 'enabled' },
    { name: 'http-submit-worker.service', binary: '/opt/GoSMPP/bin/http-submit-worker', desc: 'Kafka consumer — HTTP SMS delivery', port: 'N/A', status: 'enabled' },
    { name: 'smpp-worker.service', binary: '/opt/GoSMPP/bin/smpp-worker', desc: 'Kafka consumer — SMPP delivery + DLR', port: 'N/A', status: 'enabled' },
    { name: 'kafka (Docker)', binary: 'way2connect-kafka-dev', desc: 'Kafka message broker', port: '9092', status: 'running' },
    { name: 'zookeeper (Docker)', binary: 'way2connect-zookeeper-dev', desc: 'Kafka coordination', port: '2181', status: 'running' },
    { name: 'nginx.service', binary: '/usr/sbin/nginx', desc: 'Reverse proxy & static files', port: '80, 443', status: 'enabled' },
    { name: 'redis.service', binary: '/usr/bin/redis-server', desc: 'Sessions, balance, cache, dedup', port: '6379', status: 'enabled' },
    { name: 'mariadb.service', binary: '/usr/sbin/mariadbd', desc: 'Database server (8GB buffer pool)', port: '3306', status: 'enabled' },
  ];

  const techStack = [
    { category: 'Backend', items: ['Go 1.21+', 'Gin Web Framework', 'golang-jwt/jwt/v5', 'go-sql-driver/mysql', 'go-redis/redis/v8', 'segmentio/kafka-go', 'x/crypto/bcrypt'] },
    { category: 'Frontend', items: ['React 18', 'TailwindCSS', 'Lucide React Icons', 'React Router v6', 'Axios', 'Recharts', 'PapaParse'] },
    { category: 'Database', items: ['MariaDB 10.11', 'Redis 7', '8 databases', 'Partitioned reporting', 'Parameterized queries (no ORM)'] },
    { category: 'Queue', items: ['Apache Kafka (Docker)', 'Topics: sms.requests, sms.retries, sms.dlrs, sms.deadletter', 'DB fallback for Kafka failures'] },
    { category: 'Infrastructure', items: ['Nginx reverse proxy', 'Cloudflare DNS/SSL', 'systemd + Docker', 'SCP deployment'] },
    { category: 'Protocols', items: ['SMPP 3.4 (submit_sm, deliver_sm)', 'HTTP REST API', 'JSON SMS API', 'JWT (HS256)', 'bcrypt hashing'] },
    { category: 'Monitoring', items: ['Telegram Bot alerts', 'Kafka UI (port 8091)', 'SMPP connection logs', 'Auth audit logs', 'Worker heartbeat'] },
  ];

  return (
    <div className="max-w-7xl mx-auto">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-break { page-break-before: always; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">System Architecture Overview</h1>
          <p className="mt-1 text-gray-600">Way2Connect SMPP Platform — complete architecture and technology stack</p>
        </div>
        <div className="no-print flex gap-2">
          <button onClick={() => window.history.back()} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium">← Back</button>
          <button onClick={() => window.print()} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium">🖨️ Print</button>
        </div>
      </div>

      {/* Architecture Layers */}
      <div className="mb-8">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Architecture Layers</h2>
        <div className="space-y-4">
          {layers.map((layer, idx) => (
            <div key={idx} className="bg-white rounded-xl border border-gray-200 overflow-hidden" style={{ borderLeft: `4px solid ${layer.color}` }}>
              <div className="px-5 py-3 bg-gray-50 border-b border-gray-100">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: layer.color }} />
                  <h3 className="font-bold text-gray-900">{layer.name}</h3>
                </div>
              </div>
              <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                {layer.components.map((comp, i) => (
                  <div key={i} className="rounded-lg border border-gray-100 p-3">
                    <div className="font-semibold text-sm text-gray-900">{comp.name}</div>
                    <div className="text-xs text-gray-500 mt-1">{comp.desc}</div>
                    <div className="text-[10px] text-gray-400 mt-1 font-mono">{comp.tech}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Data Flow */}
      <div className="print-break mb-8 bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Data Flow</h2>
        <div className="space-y-2">
          {dataFlows.map((flow, i) => (
            <div key={i} className="flex items-center gap-3 text-sm">
              <span className="font-medium text-gray-700 min-w-[120px] text-right">{flow.from}</span>
              <span className="text-gray-400">→</span>
              <span className="font-medium text-gray-700 min-w-[120px]">{flow.to}</span>
              <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: `${flow.color}15`, color: flow.color }}>
                {flow.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Server & Services */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {/* Server Specs */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="text-lg font-bold text-gray-900 mb-3">Server Specifications</h2>
          <div className="space-y-2">
            {serverSpecs.map((spec, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <span className="text-gray-500">{spec.label}</span>
                <span className="font-medium text-gray-800 font-mono text-xs">{spec.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Tech Stack */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="text-lg font-bold text-gray-900 mb-3">Technology Stack</h2>
          <div className="space-y-3">
            {techStack.map((cat, i) => (
              <div key={i}>
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{cat.category}</div>
                <div className="flex flex-wrap gap-1">
                  {cat.items.map((item, j) => (
                    <span key={j} className="text-xs px-2 py-0.5 bg-gray-100 text-gray-700 rounded">{item}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Systemd Services */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8">
        <h2 className="text-lg font-bold text-gray-900 mb-4">Systemd Services</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left">
                <th className="py-2 pr-3 font-semibold text-gray-700">Service</th>
                <th className="py-2 pr-3 font-semibold text-gray-700">Binary</th>
                <th className="py-2 pr-3 font-semibold text-gray-700">Description</th>
                <th className="py-2 pr-3 font-semibold text-gray-700">Port</th>
                <th className="py-2 font-semibold text-gray-700">Auto-Start</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {systemdServices.map((svc, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="py-2 pr-3 font-mono text-xs text-blue-700">{svc.name}</td>
                  <td className="py-2 pr-3 font-mono text-xs text-gray-600 break-all">{svc.binary}</td>
                  <td className="py-2 pr-3 text-gray-600">{svc.desc}</td>
                  <td className="py-2 pr-3 text-gray-600">{svc.port}</td>
                  <td className="py-2"><span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs">{svc.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Directory Structure */}
      <div className="print-break bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">Project Directory Structure</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Backend (Go)</h3>
            <pre className="text-xs text-gray-600 bg-gray-50 rounded-lg p-3 font-mono leading-relaxed">{`backend/
├── cmd/
│   ├── server/main.go          # Backend API server
│   ├── http-submit-worker/     # HTTP delivery worker
│   ├── smpp-worker/            # SMPP delivery worker
│   ├── sms-ingest-service/     # SMS ingest pipeline
│   └── sms-dispatch-worker/    # High-volume dispatcher
├── internal/
│   ├── api/                    # HTTP handlers
│   ├── auth/                   # JWT, password, session
│   ├── config/                 # YAML config loader
│   ├── database/               # DB connections (8 DBs)
│   ├── middleware/              # Auth, rate limit, CORS
│   ├── models/                 # Data structures
│   ├── redis/                  # Redis client
│   ├── router/                 # Route definitions
│   ├── services/               # Business logic
│   ├── smpp/                   # SMPP protocol impl
│   ├── workers/                # Worker implementations
│   └── utils/                  # Shared utilities
├── config/config.yaml          # Main configuration
└── bin/                        # Compiled binaries`}</pre>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Frontend (React)</h3>
            <pre className="text-xs text-gray-600 bg-gray-50 rounded-lg p-3 font-mono leading-relaxed">{`frontend/
├── src/
│   ├── components/
│   │   ├── layout/             # Sidebar, Header
│   │   ├── admin/              # Admin components
│   │   ├── common/             # Shared components
│   │   └── support/            # Support chat
│   ├── pages/
│   │   ├── admin/              # Admin pages
│   │   ├── super/              # Super admin pages
│   │   ├── master-admin/       # Master admin pages
│   │   ├── system-flow/        # System documentation
│   │   ├── help/               # Help pages
│   │   └── knowledge-base/     # KB pages
│   ├── layouts/
│   │   └── AdminLayout.js      # Main layout + routes
│   ├── context/
│   │   └── AuthContext.js      # Auth state management
│   └── services/
│       └── api.js              # Axios API client
├── public/
└── build/                      # Production build`}</pre>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArchitectureOverview;
